/**
 * The wait while a rave's lineup loads (the user, 2026-10-07: "when loading a
 * rave I don't see the logo and the custom texts"). It was a grey skeleton of
 * the schedule — no logo, no lines. Now it is the loading screen, wearing the
 * RAVE: the MyRave mark spinning above that rave's logo, its dates and place,
 * and the typed lines — half of them about this rave — over its cover.
 *
 * The logo comes first from what this origin remembered the last time the
 * rave opened (`splash.ts`) — its bytes, held on the device, so it is there
 * with no network — else from `/tenant`, which names it since 2026-10-07.
 * No logo anywhere: the rave's name, set in type.
 */
import { useState, type CSSProperties } from 'react';
import { formatDateRange } from '@myrave/shared';
import { LoadingScreen, localDateRange } from '@/ui';
import { readJson } from '@/lib/storage';
import { LogoImg } from '@/visitor/Logo';
import { SPLASH_NAME, type SplashRave } from './splash';
import type { Tenant } from './TenantProvider';
import './RaveLoading.css';

/** The bytes held on the device first, the URL second, the name last. */
function Logo({ name, src, svg, accent }: { name: string; src?: string | null; svg?: boolean; accent?: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <span className="rave-loading-name">{name}</span>;
  if (svg) {
    return (
      <span
        className="rave-loading-mask"
        style={{
          WebkitMaskImage: `url("${src}")`,
          maskImage: `url("${src}")`,
          ...(accent ? { background: accent } : {}),
        }}
      />
    );
  }
  return <LogoImg src={src} alt="" className="rave-loading-img" onError={() => setFailed(true)} />;
}

const isSvg = (url: string) => /\.svg(\?|#|$)/i.test(url) || url.startsWith('data:image/svg');

export function RaveLoading({ label, tenant }: { label: string; tenant: Tenant | null }) {
  // The page's language as `LocaleProvider` set it on <html> (a loading screen
  // stays clear of the provider's module — step 73.6).
  const locale = (typeof document !== 'undefined' && document.documentElement.lang) || 'en';
  const remembered = readJson<SplashRave | null>(null, SPLASH_NAME, null);
  // The memory is per origin, but the same origin can host another rave
  // (the app host in festival mode): only THIS festival's memory is used.
  const mine = remembered && (!tenant || !remembered.festival || remembered.festival === tenant.festivalName)
    ? remembered : null;

  const edition = tenant?.editions?.find((e) => e.id === tenant.editionId);
  const dates = edition?.gatesOpenAt && edition.endsAt
    ? localDateRange(locale, formatDateRange(new Date(edition.gatesOpenAt), new Date(edition.endsAt), edition.timezone), new Date(edition.gatesOpenAt), new Date(edition.endsAt), edition.timezone)
    : undefined;
  // The kept dates, said in THIS page's language (a `when` kept in another language is not used when the raw
  // dates are there — the user, 2026-10-29).
  const keptDates = mine?.gatesOpenAt && mine.endsAt && mine.timezone
    ? localDateRange(locale, formatDateRange(new Date(mine.gatesOpenAt), new Date(mine.endsAt), mine.timezone), new Date(mine.gatesOpenAt), new Date(mine.endsAt), mine.timezone)
    : dates ?? mine?.when;
  const detail = mine
    ? [keptDates, mine.where].filter(Boolean).join(' · ') || undefined
    : [dates, [tenant?.venue, tenant?.country].filter(Boolean).join(' · ')].filter(Boolean).join(' · ') || undefined;

  // Never opened here: `/tenant` names the logo now, so it is still the rave's.
  const brand = mine
    ? <Logo name={mine.festival ?? mine.name} src={mine.logoData ?? mine.logoUrl} svg={mine.logoSvg} accent={mine.accent} />
    : tenant
      ? <Logo name={tenant.festivalName} src={tenant.logoUrl} svg={tenant.logoUrl ? isSvg(tenant.logoUrl) : false} />
      : undefined;

  // The rave's colour for the greeting's name (`.ui-wait-name`) before its line-up has set `--accent` (2026-11-02).
  return (
    <div style={{ display: 'contents', ...(mine?.accent ? { '--wait-accent': mine.accent } : {}) } as CSSProperties}>
      <LoadingScreen label={label} brand={brand} detail={detail} />
    </div>
  );
}
